import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
	Alert,
	Box,
	IconButton,
	LinearProgress,
	Paper,
	Stack,
	Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useAssetId } from "@/setup/hooks/useAssetId";
import { DEFAULT_MILL, type MillId } from "@/setup/mill-config/mill-config";
import { routeProductsTestShackManager } from "@/setup/routes/products.routes";
import { useShiftInspectionQuery } from "../../hooks/queries/useShiftInspectionsQuery";
import {
	SECTION_SPEC_STATUS,
	type SectionSpecDetail,
} from "@/shared/models/SectionSpec";
import { InspectionFormSplitLayout } from "../components/InspectionFormSplitLayout";
import type { InspectionFormSubmission } from "../../models/InspectionForm";
import { SheetSectionDetailsPanel } from "./components/SheetSectionDetailsPanel";
import type { UpsertInspectionPayload } from "../../models/Inspection";
import { useUpsertInspectionMutation } from "../../hooks/mutations/useInspectionMutations";

const FORM_PANEL_ID = "sheet-inspection";
const DETAILS_PANEL_ID = "sheet-section-details";
const SHEET_LAYOUT_ID = "test-shack-sheet";
const SHEET_GROUP_STYLE = { height: "100%" };
const SHEET_CREATE_RESET = { version: 0 };
const noop = () => undefined;

export const TestShackSheetPage = () => {
	const assetId = (useAssetId() as MillId) ?? DEFAULT_MILL.id;
	const { profileInspectionId } = useParams();
	return (
		<TestShackSheetPageContent
			key={`${assetId}:${profileInspectionId}`}
			assetId={assetId}
			profileInspectionId={Number(profileInspectionId) || null}
		/>
	);
};

interface ContentProps {
	assetId: MillId;
	profileInspectionId: number | null;
}

const TestShackSheetPageContent = ({
	assetId,
	profileInspectionId,
}: ContentProps) => {
	const navigate = useNavigate();

	const [error, setError] = useState("");
	const [specDetail, setSpecDetail] = useState<SectionSpecDetail | null>(null);
	const [specPending, setSpecPending] = useState(true);
	const handleSpecChange = useCallback(
		(detail: SectionSpecDetail | null, pending: boolean) => {
			setSpecDetail(detail);
			setSpecPending(pending);
		},
		[],
	);
	const submitInProgressRef = useRef(false);
	const onError = (queryError: Error) => setError(queryError.message);

	const {
		data,
		isLoading,
		error: e,
		isError,
	} = useShiftInspectionQuery({ assetId, profileInspectionId });
	const inspection = data?.inspections[0] ?? null;

	const spec =
		specDetail?.spec?.SectionId === inspection?.SectionId
			? (specDetail?.spec ?? null)
			: null;
	const hasConfirmedRevisions =
		specDetail?.versions.some(
			(version) => version.StatusCode !== SECTION_SPEC_STATUS.DRAFT,
		) ?? false;

	const saveMutation = useUpsertInspectionMutation({ onError });

	const backToManager = () =>
		navigate(
			inspection?.ShiftId
				? `${routeProductsTestShackManager}?mode=shift&shift=${inspection.ShiftId}`
				: routeProductsTestShackManager,
		);

	const handleSubmit = (payload: InspectionFormSubmission) => {
		if (!inspection || submitInProgressRef.current || saveMutation.isPending)
			return;

		const snapshot: UpsertInspectionPayload = {
			profileInspectionId: inspection.ProfileInspectionId,
			assetId,
			sectionId: payload.sectionId,
			heatId: payload.heatId,
			inspectedAt: new Date(payload.inspectedAt),
			heatName: null,
			measuredLbsPerFoot: payload.measuredLbsPerFoot,
			weightProfileItemId: specDetail?.weightProfileItemId ?? null,
			comments: payload.comments,
			readings: payload.readings.map((reading) => ({ ...reading })),
		};

		submitInProgressRef.current = true;
		saveMutation.mutate(snapshot, {
			onSettled: () => {
				submitInProgressRef.current = false;
			},
		});
	};

	useEffect(() => {
		if (e && isError) {
			setError(e.message);
		}
	}, [isError, e]);

	const detailsPanel = (
		<Paper elevation={2} sx={{ p: 2, height: "100%", overflow: "auto" }}>
			<SheetSectionDetailsPanel
				sectionId={inspection?.SectionId ?? null}
				spec={spec}
				hasConfirmedRevisions={hasConfirmedRevisions}
				pending={specPending}
			/>
		</Paper>
	);

	return (
		<>
			<HelmetDexteel
				title={
					inspection
						? `${inspection.SectionName} - Test Shack Sample`
						: "Test Shack Sample"
				}
			/>
			<Box
				sx={{
					boxSizing: "border-box",
					display: "grid",
					gap: 1,
					gridTemplateRows: "auto minmax(0, 1fr)",
					height: "calc(100vh - 64px)",
					px: 2,
					py: 2,
				}}
			>
				<Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
					<IconButton
						size="small"
						title="Back to the manager"
						aria-label="Back to the manager"
						onClick={backToManager}
					>
						<ArrowBackIcon fontSize="small" />
					</IconButton>
					<Typography variant="h5" fontWeight={600}>
						{inspection
							? `#${inspection.ProfileInspectionId} · ${inspection.SectionName}`
							: "Test Shack Sample"}
					</Typography>
				</Stack>

				<Box sx={{ minHeight: 0, overflow: "auto" }}>
					{isLoading && <LinearProgress aria-label="Loading the sample" />}
					{isError && (
						<Alert severity="error" role="alert">
							The sample could not be loaded.
						</Alert>
					)}
					{!isLoading && !isError && inspection === null && (
						<Alert severity="info" role="status">
							This sample no longer exists.
						</Alert>
					)}

					{inspection && (
						<InspectionFormSplitLayout
							formOptions={{
								assetId,
								sectionId: inspection.SectionId,
								currentSectionId: null,
								onSpecChange: handleSpecChange,
								editing: inspection,
								createReset: SHEET_CREATE_RESET,
								saving: saveMutation.isPending,
								onCancelEdit: backToManager,
								onSectionChange: noop,
								onSubmit: handleSubmit,
							}}
							formLayout="stacked"
							detailsPanel={detailsPanel}
							layoutId={SHEET_LAYOUT_ID}
							formPanelId={FORM_PANEL_ID}
							detailsPanelId={DETAILS_PANEL_ID}
							groupStyle={SHEET_GROUP_STYLE}
						/>
					)}
				</Box>
			</Box>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
